import React, {createRef, useContext, useEffect, useState} from 'react';
import {
  Breakpoints,
  styled,
} from '@volkswagen-onehub/components-core';
import { formatPostalCode, validatePostalCode } from '../../UserLocation/utils';
import {PrivacyPolicyLink} from '../../Home/PrivacyPolicyLink';
import {ApplyFilters} from '../common/ApplyFilters';
import {getTranslations} from '../../translations';
import {useSearchParams} from 'react-router-dom';
import { useFeatureServices } from '../../use-feature-services';
import { createConstantData, createPartner } from '../../utils/get-tracking-event-data';
import { getSingleSelectedDealer } from '../../utils/get-single-selected-dealer';
import { VolksKlick } from '../../Home/VolksKlick';
import { InventoryType } from '../common/InventoryType';
import { LayerCloseHandle } from '../../types/layers';
import { PostalCodeInput } from '../../UserLocation/PostalCodeInput';
import { UserLocationContext } from '../../UserLocation/UserLocationContext';
import { setReturnVisitorCookie } from '../../Cookies';
import { useFiltering } from '../../utils/use-filtering';
import { LocationContext } from '../../UserLocation/LocationContext';

const StyledButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
`;

const StyledContainer = styled.div`
  padding: ${(props) => props.theme.size.dynamic0250} ${(props) => props.theme.size.dynamic0100};
  @media (min-width: ${Breakpoints}px) {
    padding: ${(props) => props.theme.size.dynamic0250};
  }
  width: 100%;
  display: flex;
  flex-direction: column;
  row-gap: 32px;
`;

const PostalCodeWrapper = styled.div`
  margin: 0 auto;
  width: 100%;
  max-width: 60%;
`;
interface Props {
  close: LayerCloseHandle;
}

export const Search = ({ close }: Props) => {
  const { volksklick } = getTranslations();

  const { tracking } = useFeatureServices();
  const locationCtx = useContext(LocationContext);
  const userLocationContext = useContext(UserLocationContext);
  const { postalCode, distance, parseLocationParams } = userLocationContext;

  const { applySelectedFilters } = useFiltering();

  const [searchParams] = useSearchParams();
  const postalCodeRef = createRef<HTMLInputElement>();

  const [postalCodeValue, setPostalCodeValue] = useState(postalCode || "");
  const [hasError, setHasError] = useState(false);
  const [ fetchingGeolocation, setFetchingGeolocation ] = useState(false);

  const partner = getSingleSelectedDealer();

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    handleSubmit(e);
  };

  const handleSubmit = (e: React.SyntheticEvent<HTMLButtonElement> | React.KeyboardEvent<HTMLInputElement>) => {
    e.preventDefault();

    const valid = validatePostalCode(postalCodeValue);

    if (!valid) {
      setHasError(true);
      postalCodeRef?.current?.focus();
    } else {
      const formattedPostalCode = formatPostalCode(postalCodeValue, true);
      setPostalCodeValue(formattedPostalCode);

      locationCtx?.set(formattedPostalCode);

      applySelectedFilters();

      tracking.track('VWBasic_FilterSearchButton_Click', {
        ...createConstantData(),
        partner: createPartner(partner),
        link: {
          url: window.location.href,
          name: volksklick.cta
        },
        filter: {
          type: "complex",
          name: "location",
          values: [postalCode, distance],
          filterlist: [
            {
              type: "single_value",
              name: "ZIP",
              values: [postalCode]
            },
            {
              type: "single_value",
              name: "DI",
              values: [distance]
            }
          ]
        }
      })

      close();
    }
  };

  const handleEnter = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
     handleSubmit(e);
    }
  };

  useEffect(() => {
    if (parseLocationParams) {
      parseLocationParams();
    }
    return () => setReturnVisitorCookie();
  }, []);

  useEffect(() => {}, [searchParams]);

  return (
    <StyledContainer>
      <VolksKlick />
      <InventoryType initialSearch />
      <PostalCodeWrapper>
        <PostalCodeInput
          postalCodeValue={postalCodeValue}
          setPostalCodeValue={setPostalCodeValue}
          handleEnter={handleEnter}
          hasError={hasError}
          setHasError={setHasError}
          onHomeModal={true}
          fetchingGeolocation={fetchingGeolocation}
          setFetchingGeolocation={setFetchingGeolocation}
        />
      </PostalCodeWrapper>
      <ApplyFilters
        label={volksklick.cta}
        ariaLabel={volksklick.cta}
        handleClick={handleClick}
        disabled={fetchingGeolocation}
      />
      <StyledButtonWrapper>
        <PrivacyPolicyLink />
      </StyledButtonWrapper>
    </StyledContainer>
  );
};
